import React, { useState, useEffect, useCallback, useRef } from "react";
import BaseTile from '../../../components/Tile/BaseTile';
import TileContent, { TileStatusContent } from '../../../components/Tile/TileContent';
import RefreshButton from '../../../components/Tile/RefreshButton';
import { useGlobalAPI } from "../../../hooks/useGlobalAPI";
import { useActivityFrequencyRatioKPI } from "./hooks/useActivityFrequencyRatioKPI";
import { useOcelMetadata } from '../../../stores/OcelContext';
import MultiSelectDropdown from '../../../components/ui/MultiSelectDropdown';

const ActivityFrequencyRatioKPI = ({ activeFile, onRefresh, onGetHeaderActions }) => {
  const api = useGlobalAPI(activeFile);
  const { data, loading, error, hasFetched, fetchKPIData, reset } = useActivityFrequencyRatioKPI(activeFile);
  const metadata = useOcelMetadata(activeFile);
  const [objectTypeActivities, setObjectTypeActivities] = useState({});
  const [activityTypesLoading, setActivityTypesLoading] = useState(false);
  const [objectType, setObjectType] = useState('');
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [customTitle, setCustomTitle] = useState('Activity Frequency per Object Type');
  const [isSettingsCollapsed, setIsSettingsCollapsed] = useState(() => {
    const saved = localStorage.getItem("activityFrequencyRatioKPI_settingsCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [filterText, setFilterText] = useState('');

  const [objectTypes, setObjectTypes] = useState([]);

  useEffect(() => {
    localStorage.setItem(
      "activityFrequencyRatioKPI_settingsCollapsed",
      JSON.stringify(isSettingsCollapsed)
    );
  }, [isSettingsCollapsed]);

  useEffect(() => {
    reset();
    setObjectType('');
    setSelectedActivities([]);
    setCustomTitle('Activity Frequency per Object Type');
    setObjectTypes([]);
    setFilterText('');
    hasAutoSelected.current = false;
  }, [activeFile, reset]);

  useEffect(() => {
    if (!metadata?.object_types) return;
    setObjectTypes(Object.keys(metadata.object_types));
  }, [metadata]);

  useEffect(() => {
    const fetchObjectTypeActivities = async () => {
      if (!activeFile) {
        setObjectTypeActivities({});
        return;
      }

      setActivityTypesLoading(true);
      try {
        console.log('🔄 Fetching object-type activities for:', activeFile);
        const response = await api.get('/object-type-activities', {}, 'object type activities');
        
        if (response?.objectTypeActivities) {
          setObjectTypeActivities(response.objectTypeActivities);
          console.log('✅ Object-type activities loaded:', response.objectTypeActivities);
          
          Object.keys(response.objectTypeActivities).forEach(type => {
            console.log(`📋 ${type}: ${response.objectTypeActivities[type]?.length || 0} activities`);
          });
        } else {
          console.warn('⚠️ No objectTypeActivities in response');
          setObjectTypeActivities({});
        }
      } catch (error) {
        console.error('❌ Error fetching object-type activities:', error);
        setObjectTypeActivities({});
      } finally {
        setActivityTypesLoading(false);
      }
    };

    fetchObjectTypeActivities();
  }, [activeFile]);

  useEffect(() => {
    if (objectTypes.length > 0 && objectType && !objectTypes.includes(objectType)) {
      setObjectType(objectTypes[0]);
    }
  }, [objectTypes, objectType]);

  useEffect(() => {
    console.log('🔄 Object type changed to:', objectType, 'Available activities:', objectTypeActivities[objectType]?.length || 0);
    setSelectedActivities([]);
    setFilterText('');
    hasAutoSelected.current = false;
  }, [objectType]);

  const hasAutoSelected = useRef(false);
  useEffect(() => {
    const currentActivities = objectTypeActivities[objectType] || [];
    
    console.log('🔍 Auto-selection check:', {
      objectType,
      currentActivitiesCount: currentActivities.length,
      activityTypesLoading,
      hasAutoSelectedCurrent: hasAutoSelected.current,
      selectedActivitiesLength: selectedActivities.length,
      allActivities: currentActivities
    });
    
    if (
      currentActivities.length > 0 &&
      !activityTypesLoading &&
      !hasAutoSelected.current &&
      objectType &&
      selectedActivities.length === 0
    ) {
      // Select first 3 activities or all if less than 3 are available
      const defaultCount = Math.min(currentActivities.length, 3);
      const defaultActivities = currentActivities.slice(0, defaultCount);
      console.log('🎯 Auto-selecting default activities for', objectType, ':', defaultActivities);
      setSelectedActivities(defaultActivities);
      hasAutoSelected.current = true;
    } else {
      console.log('⏸️ Auto-selection skipped - conditions not met');
    }
  }, [objectTypeActivities, objectType, activityTypesLoading, selectedActivities.length]);

  const toggleSettings = () => {
    setIsSettingsCollapsed((prev) => !prev);
  };

  const handleUpdate = useCallback(async () => {
    console.log('� handleUpdate called with:', {
      objectType,
      selectedActivities,
      selectedActivitiesLength: selectedActivities.length,
      customTitle
    });
    
    if (!objectType) {
      console.warn('⚠️ Prevented KPI request with no object type selected');
      return;
    }
    
    if (!selectedActivities || selectedActivities.length === 0) {
      console.warn('⚠️ Prevented KPI request with empty activities');
      return;
    }
    
    await fetchKPIData(objectType, selectedActivities);
  }, [fetchKPIData, objectType, selectedActivities, customTitle]);

  const handleReset = useCallback(() => {
    setObjectType('');
    setSelectedActivities([]);
    setCustomTitle('Activity Frequency per Object Type');
    reset();
  }, [reset]);

  const toggleActivitySelection = useCallback((activity) => {
    setSelectedActivities(prev => {
      if (prev.includes(activity)) {
        return prev.filter(a => a !== activity);
      } else {
        return [...prev, activity];
      }
    });
  }, []);

  const selectAllActivities = useCallback(() => {
    const currentActivities = objectTypeActivities[objectType] || [];
    setSelectedActivities([...currentActivities]);
  }, [objectTypeActivities, objectType]);

  const clearAllActivities = useCallback(() => {
    setSelectedActivities([]);
  }, []);
  
  const isActivitySelected = useCallback((activity) => {
    return selectedActivities.includes(activity);
  }, [selectedActivities]);


  useEffect(() => {
    if (onRefresh) {
      onRefresh(handleUpdate);
    }
  }, [onRefresh, handleUpdate]);

  useEffect(() => {
    if (onGetHeaderActions) {
      onGetHeaderActions(() => (
        <div className="flex items-center gap-2">
          <RefreshButton onClick={handleUpdate} loading={loading} />
          <button
            onClick={toggleSettings}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors duration-200"
            title={isSettingsCollapsed ? "Show Settings" : "Hide Settings"}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
      ));
    }
  }, [onGetHeaderActions, handleUpdate, loading, toggleSettings, isSettingsCollapsed]);


  const showInitialButton = !hasFetched && isSettingsCollapsed;
  const shouldShowError = error && hasFetched;

  return (
    <BaseTile
      loading={loading}
      error={shouldShowError}
      minHeight="auto"
      padding="sm"
      className=""
    >
      <TileContent layout="flex" direction="column" grow>
        {/* Initial Load Button */}
        {showInitialButton && (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={() => setIsSettingsCollapsed(false)}
              disabled={loading}
              className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Configure KPI
            </button>
          </div>
        )}

        {/* Control Bar - Always Visible */}
        {!showInitialButton && (
          <div className="flex items-start gap-3 mb-4 pb-3 border-b border-gray-200">
            <button
              onClick={handleUpdate}
              disabled={loading || !objectType || selectedActivities.length === 0 || !customTitle.trim()}
              className={`px-4 py-2 text-white text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 ${
                objectType && selectedActivities.length > 0 && !loading && customTitle.trim()
                ? "bg-primary-500 hover:bg-primary-600 focus:ring-primary-500"
                : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              {loading ? "Calculating..." : "Calculate KPI"}
            </button>
            <button
              onClick={handleReset}
              disabled={loading || !hasFetched}
              className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 ${
                hasFetched && !loading 
                ? "bg-gray-500 text-white hover:bg-gray-600 focus:ring-gray-500" 
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              Reset
            </button>
            <button
              onClick={toggleSettings}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-expanded={!isSettingsCollapsed}
              aria-label={isSettingsCollapsed ? "Show settings" : "Hide settings"}
            >
              <svg
                className="w-4 h-4 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.5 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span>{isSettingsCollapsed ? "Settings" : "Hide"}</span>
            </button>
          </div>
        )}

        {/* Settings Panel */}
        {!isSettingsCollapsed && !showInitialButton && (
          <div className="overflow-visible transition-all duration-200 ease-in-out mb-4">
            <div className="space-y-4 pb-4 border-b border-gray-200">
              {/* KPI Title - At the top like in DateDeviationKPI */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  KPI Title *
                </label>
                <input
                  type="text"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Order Processing Intensity"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Custom name for your KPI widget
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                {/* Object Type Selection */}
                <div className="z-10">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Object Type *
                  </label>
                  <div className="relative">
                    <select
                      value={objectType}
                      onChange={(e) => setObjectType(e.target.value)}
                      className="appearance-none w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={loading}
                    >
                      <option value="" disabled>
                        Select Object Type
                      </option>
                      {objectTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Objects of this type will be counted for the object count
                  </p>
                </div>

                {/* Activities Dropdown */}
                <div className="z-10">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Activities *
                  </label>
                  <MultiSelectDropdown
                    options={objectTypeActivities[objectType] || []}
                    selectedValues={selectedActivities}
                    onSelectionChange={setSelectedActivities}
                    placeholder={!objectType ? "Select an object type first..." : "Select activities..."}
                    disabled={!objectType || loading}
                    itemType="activities"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {!objectType 
                      ? "Select an object type to view activities" 
                      : "Activities that will be counted for frequency calculation"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        {data && !loading && (
          <div className="space-y-4">
            {/* Main KPI Value */}
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">
                {data.ratio.toFixed(2)}
              </div>
              <div className="text-sm text-gray-500">
                Activity Frequency / Object Count
              </div>
            </div>

            {/* Breakdown Details */}
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-xl font-semibold text-blue-900">
                  {data.total_activity_frequency !== undefined ? data.total_activity_frequency.toLocaleString() : 
                   data.numerator !== undefined ? data.numerator.toLocaleString() : '0'}
                </div>
                <div className="text-sm text-blue-700">
                  Total Activity Frequency
                </div>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="text-xl font-semibold text-green-900">
                  {data.total_object_count !== undefined ? data.total_object_count.toLocaleString() : 
                   data.denominator !== undefined ? data.denominator.toLocaleString() : '0'}
                </div>
                <div className="text-sm text-green-700">
                  Total Object Count ({data.object_type})
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Empty State with Instructional Placeholders */}
        {!data && !loading && !showInitialButton && (
          <TileStatusContent
            status="empty"
            title="No Activity Frequency Ratio Data"
            message="Select an object type and activities in the settings panel, then calculate the KPI."
            icon={
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                      d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
          />
        )}
      </TileContent>
    </BaseTile>
  );
};

export default ActivityFrequencyRatioKPI;